import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import FixedAgent from "../../utils/api/FixedAgent";

const getDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const getYearToDateRange = (date = new Date()) => ({
  from_date: `${date.getFullYear()}-01-01`,
  to_date: getDateKey(date),
});

const getPolicyCount = (response) => {
  const rows = response?.data?.data;
  if (!Array.isArray(rows)) return null;

  return rows.reduce((total, row) => total + Number(row?.count || 0), 0);
};

const getRequestError = (error) =>
  error?.response?.data?.message || error?.message || "Unable to load reward progress.";

export const fetchRewardsPolicyCount = createAsyncThunk(
  "rewards/fetchPolicyCount",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await FixedAgent.getPolicyCountsByPlatform({
        token,
        ...getYearToDateRange(),
      });
      const policyCount = getPolicyCount(response);

      if (policyCount === null) {
        return rejectWithValue("The policy-count response was invalid.");
      }

      return policyCount;
    } catch (error) {
      return rejectWithValue(getRequestError(error));
    }
  },
  {
    condition: ({ token }, { getState }) => {
      if (!token) return false;

      const rewardsState = getState().rewards;
      const today = getDateKey();

      return rewardsState.status !== "loading" && rewardsState.requestDate !== today;
    },
  },
);

const initialState = {
  policyCount: null,
  status: "idle",
  error: "",
  requestDate: null,
};

const rewardsSlice = createSlice({
  name: "rewards",
  initialState,
  reducers: {
    resetRewardsProgress: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRewardsPolicyCount.pending, (state) => {
        state.status = "loading";
        state.error = "";
        state.requestDate = getDateKey();
      })
      .addCase(fetchRewardsPolicyCount.fulfilled, (state, action) => {
        state.policyCount = action.payload;
        state.status = "succeeded";
        state.error = "";
      })
      .addCase(fetchRewardsPolicyCount.rejected, (state, action) => {
        state.policyCount = null;
        state.status = "failed";
        state.error = action.payload || action.error.message || "Unable to load reward progress.";
      })
      .addCase("User Data/logout", () => initialState);
  },
});

export const rewardsActions = rewardsSlice.actions;
export default rewardsSlice.reducer;
